/**
 * An admin's correction (DESIGN-toil.md G): + or −, with a reason that sits in the history
 * beside it. Also how balances brought over from another system go in — hence "This time
 * doesn't expire" (assumption 5). Not a <form>: it lives inside other screens.
 */
import React, { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { toilApi } from '../../lib/api';
import { fmtToil, toMinutes } from '../../lib/toil';

export default function CorrectionForm({ userId, onDone, onCancel }) {
  const [sign, setSign] = useState(1);
  const [h, setH] = useState('');
  const [m, setM] = useState('');
  const [reason, setReason] = useState('');
  const [noExpiry, setNoExpiry] = useState(false);
  const [saving, setSaving] = useState(false);
  const amount = toMinutes(h, m);

  const save = async () => {
    if (!amount) { toast.error('Enter the time to add or take away'); return; }
    if (!reason.trim()) { toast.error('Say why — the reason sits in their history'); return; }
    setSaving(true);
    try {
      await toilApi.correct({ user_id: userId, minutes: sign * amount, reason: reason.trim(),
        no_expiry: sign > 0 && noExpiry });
      toast.success('Correction added');
      if (onDone) onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not add the correction');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-xl border border-border p-4 space-y-3 bg-secondary/20" data-testid="toil-correction">
      <p className="font-semibold text-sm">Correction</p>
      <div className="flex flex-wrap items-center gap-2">
        <div className="inline-flex rounded-full border border-border overflow-hidden">
          <button type="button" onClick={() => setSign(1)} data-testid="toil-correction-add"
            className={`px-3 py-1 text-sm font-semibold ${sign > 0 ? 'bg-foreground text-background' : ''}`}>Add</button>
          <button type="button" onClick={() => setSign(-1)} data-testid="toil-correction-take"
            className={`px-3 py-1 text-sm font-semibold ${sign < 0 ? 'bg-foreground text-background' : ''}`}>Take away</button>
        </div>
        <input type="number" min="0" inputMode="numeric" value={h} onChange={(e) => setH(e.target.value)}
          className="input-hive w-20 rounded-md border border-input bg-background px-2 py-1.5 text-sm" aria-label="Hours" />
        <span className="text-sm text-muted-foreground">hours</span>
        <input type="number" min="0" max="59" step="5" inputMode="numeric" value={m} onChange={(e) => setM(e.target.value)}
          className="input-hive w-20 rounded-md border border-input bg-background px-2 py-1.5 text-sm" aria-label="Minutes" />
        <span className="text-sm text-muted-foreground">minutes</span>
      </div>
      <input type="text" value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500}
        placeholder="Reason, e.g. Brought over from Timetastic" data-testid="toil-correction-reason"
        className="input-hive w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
      {sign > 0 && (
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={noExpiry} onChange={(e) => setNoExpiry(e.target.checked)} className="accent-primary" />
          This time doesn't expire
        </label>
      )}
      <div className="flex justify-end gap-2">
        {onCancel && <Button type="button" variant="outline" size="sm" onClick={onCancel}>Cancel</Button>}
        <Button type="button" size="sm" className="btn-primary" onClick={save} disabled={saving} data-testid="toil-correction-save">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : `${sign > 0 ? 'Add' : 'Take away'} ${amount ? fmtToil(amount) : ''}`.trim()}
        </Button>
      </div>
    </div>
  );
}
